import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, Building, Wallet,
  CreditCard, Landmark, PiggyBank, BarChart3, Loader2, RefreshCw
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const fmt = (v) => {
  const n = parseFloat(v) || 0;
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtFull = (v) => parseFloat(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const ASSET_ICONS = { "Stocks": TrendingUp, "Crypto": BarChart3, "Real Estate": Building, "Savings & Emergency": PiggyBank, "Investment Goals": TrendingUp };
const LIABILITY_ICONS = { "Loans": Landmark, "Mortgages": Building, "Credit Cards": CreditCard, "Debt Goals": Wallet };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
      <p className="text-zinc-400 mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400 capitalize">{p.dataKey.replace('_', ' ')}:</span>
          <span className="text-white font-mono">{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function NetWorth() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [nwRes, histRes] = await Promise.all([
        api.get("/net-worth"),
        api.get("/net-worth/history")
      ]);
      setData(nwRes.data);
      setHistory(histRes.data);
    } catch { toast.error("Failed to load net worth"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const seedHistory = async () => {
    try {
      await api.post("/net-worth/seed-history");
      toast.success("Historical data generated");
      loadData();
    } catch { toast.error("Failed to seed data"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>
  );

  const changeFromFirst = history.length >= 2
    ? data.net_worth - history[0].net_worth
    : 0;
  const changePct = history.length >= 2 && history[0].net_worth !== 0
    ? ((changeFromFirst / Math.abs(history[0].net_worth)) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6" data-testid="net-worth-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Net Worth</h1>
          <p className="text-sm text-zinc-500 mt-1">Complete financial overview</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-nw-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Net Worth Hero */}
      {data && (
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5" />
          <CardContent className="p-8 relative">
            <div className="text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-[0.2em] mb-2">Total Net Worth</p>
              <p className={`text-5xl md:text-6xl font-bold font-mono tracking-tight ${data.net_worth >= 0 ? 'text-white' : 'text-red-400'}`} data-testid="net-worth-total">
                {fmtFull(data.net_worth)}
              </p>
              {history.length >= 2 && (
                <div className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-sm font-mono ${changeFromFirst >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`} data-testid="nw-change">
                  {changeFromFirst >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {changeFromFirst >= 0 ? '+' : ''}{fmtFull(changeFromFirst)} ({changePct}%)
                </div>
              )}
              <p className="text-xs text-zinc-600 mt-2">as of {data.snapshot_date}</p>
            </div>

            {/* Assets / Liabilities Summary */}
            <div className="grid grid-cols-2 gap-8 mt-8 max-w-md mx-auto">
              <div className="text-center">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Assets</p>
                <p className="text-2xl font-bold text-emerald-400 font-mono mt-1" data-testid="total-assets">{fmtFull(data.total_assets)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Liabilities</p>
                <p className="text-2xl font-bold text-red-400 font-mono mt-1" data-testid="total-liabilities">{fmtFull(data.total_liabilities)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historical Chart */}
      <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-cyan-400" /> Net Worth Over Time
            </CardTitle>
            {history.length < 2 && (
              <Button onClick={seedHistory} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm text-xs" data-testid="seed-history-btn">
                Generate Demo History
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {history.length >= 2 ? (
            <div className="h-[280px]" data-testid="nw-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="assetGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#71717a' }} tickFormatter={(d) => { const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-US', { month: 'short' }); }} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717a' }} tickFormatter={fmt} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#71717a' }} />
                  <Area type="monotone" dataKey="total_assets" name="Assets" stroke="#06b6d4" fill="url(#assetGrad)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="net_worth" name="Net Worth" stroke="#10b981" fill="url(#nwGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="total_liabilities" name="Liabilities" stroke="#ef4444" fill="none" strokeWidth={1.5} strokeDasharray="4 4" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-zinc-600 text-sm">
              <div className="text-center">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Not enough data for chart</p>
                <p className="text-xs text-zinc-700 mt-1">Click "Generate Demo History" or check back tomorrow</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown Cards */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assets Breakdown */}
          <Card className="bg-[#0A0A0A] border-emerald-500/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" /> Assets Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.assets_breakdown.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-4">No assets tracked yet</p>
              ) : (
                <div className="space-y-3">
                  {data.assets_breakdown.map((item) => {
                    const Icon = ASSET_ICONS[item.category] || DollarSign;
                    const pct = data.total_assets > 0 ? (item.amount / data.total_assets * 100).toFixed(1) : 0;
                    return (
                      <div key={item.category}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm text-zinc-300">{item.category}</span>
                            <span className="text-xs text-zinc-600">({item.count})</span>
                          </div>
                          <span className="text-sm font-mono text-emerald-400">{fmtFull(item.amount)}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500/50 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-0.5 text-right">{pct}%</p>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="text-sm font-medium text-white">Total Assets</span>
                    <span className="text-sm font-mono text-emerald-400 font-bold">{fmtFull(data.total_assets)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Liabilities Breakdown */}
          <Card className="bg-[#0A0A0A] border-red-500/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-400" /> Liabilities Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.liabilities_breakdown.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-4">No liabilities — great job!</p>
              ) : (
                <div className="space-y-3">
                  {data.liabilities_breakdown.map((item) => {
                    const Icon = LIABILITY_ICONS[item.category] || DollarSign;
                    const pct = data.total_liabilities > 0 ? (item.amount / data.total_liabilities * 100).toFixed(1) : 0;
                    return (
                      <div key={item.category}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-red-400" />
                            <span className="text-sm text-zinc-300">{item.category}</span>
                            <span className="text-xs text-zinc-600">({item.count})</span>
                          </div>
                          <span className="text-sm font-mono text-red-400">{fmtFull(item.amount)}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500/50 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-0.5 text-right">{pct}%</p>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="text-sm font-medium text-white">Total Liabilities</span>
                    <span className="text-sm font-mono text-red-400 font-bold">{fmtFull(data.total_liabilities)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
